import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { hairline, media, radius, rem, space, typeBreakpoint } from '@gbi/ui';
import tokens from '../../../assets/styles/tokens';
import Button from '../../Button/Button';
import { RadioCircle } from '../../InputFields/Radio';
import { ReactComponent as AlertCloseIconSvg } from '../../../assets/images/priceAlerts/alert-close-icon.svg';
import { ReactComponent as InfoGttIconSvg } from '../../../assets/images/priceAlerts/info-gtt.svg';
import { ReactComponent as SearchSvg } from '../../../assets/images/gtt/search.svg';
import { ReactComponent as CheckCircleBlueSvg } from '../../../assets/images/svg/check-circle-blue.svg';
import { ReactComponent as ArrowDownSvg } from '../../../assets/images/gtt/arrow_down.svg';
import { ReactComponent as PlusIconSvg } from '../../../assets/images/svg/quantity-plus.svg';
import { ReactComponent as MinusIconSvg } from '../../../assets/images/svg/quantity-minus.svg';
import { ReactComponent as VaultSvg } from '../../../assets/images/svg/vault.svg';
import { ReactComponent as HandCoinsSvg } from '../../../assets/images/svg/hand-coins.svg';
import { ReactComponent as DiscountSvg } from '../../../assets/images/gtt/discount.svg';
import themeColors from '../../../assets/styles/themeColors';
import { mediaBelow, mediaUp } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import { uButtonClear, uOutlineNone } from '../../../assets/styles/utility';

// --- Design library (@gbi/ui) ------------------------------------------------
// The Buy at Target Price screens follow the designer's "Target Price (USE THIS)"
// frames (Figma: Buy Wizard - Trigger Order). Text uses the library's text
// styles, which step up at 375 and 430. Spacing does not step: it uses rem(),
// which --legacy-scale does not shrink. pxToRem(16) renders 10.5px at 320.

// The wizard's side padding: 16px at every width.
export const GUTTER = rem(space[16]);

// Wizard header (BasicModal headerCss/titleCss): 8px/16px padding and a
// title/400 title, as in the frames.
export const wizardHeaderCss = `
  padding: ${rem(space[8])} ${GUTTER};
  border-bottom: ${hairline} solid ${tokens.color.border.subtle};
`;

export const wizardTitleCss = `
  ${tokens.type.title[400]}
  color: ${tokens.color.text.primary};
`;

// The designer tints green callouts (savings, volume discount) with 5% of the
// flag green. Built from the token, since color-mix() is too new for our
// browsers.
export const flagTint = (alpha) => {
  const hex = tokens.color.status.flag.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Full-width wizard button text: title/400 with 12px above and below, so the
// button grows with its text (45px at 320, 47px at 375, 50px at 430).
export const wizardButtonCss = `
  height: auto;
  min-width: 0;
  padding: ${rem(space[12])} ${GUTTER};
  ${tokens.type.title[400]}
`;

// Row of single-select metal pills with a trailing search affordance.
export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${rem(space[16])};
  margin-bottom: ${rem(space[12])};
`;

export const MetalChips = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(space[12])};
  flex: 1;
`;

// Outlined pill, label/300. The selected chip has a brand border and text
// and a trailing check icon (no fill); the others a subtle border.
export const MetalChip = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-items: center;
  gap: ${rem(space[4])};
  background-color: transparent;
  ${tokens.type.label[300]}
  color: ${({ isSelected }) =>
    isSelected ? tokens.color.text.brand : tokens.color.text.label};
  padding: ${rem(space[6])} ${rem(space[8])};
  border: ${hairline} solid
    ${({ isSelected }) =>
      isSelected ? tokens.color.action.primary : tokens.color.border.subtle};
  border-radius: ${radius.base};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

// The SVG bakes in #1C4792; the frame draws the check in the inverse surface
// colour.
export const MetalChipCheck = styled(CheckCircleBlueSvg)`
  &&& {
    width: 16px;
    height: 16px;

    path {
      fill: ${tokens.color.surface.inverse};
    }
  }
`;

export const SearchToggle = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${tokens.color.text.primary};
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
`;

// 38px at 320 and 52px from 375, like the wizard's other inputs.
export const SearchInput = styled.input`
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 ${rem(space[20])} 0 ${GUTTER};
  border: none;
  border-radius: ${radius.base};
  background-color: ${tokens.color.surface.sunken};
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};

  ::placeholder {
    ${tokens.type.body[200]}
    color: ${tokens.color.text.label};
  }

  ${media.up(typeBreakpoint[375])} {
    height: 52px;
  }
`;

export const SearchClearButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  position: absolute;
  right: ${rem(space[12])};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${tokens.color.text.label};

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const SearchClearIcon = styled(AlertCloseIconSvg)``;

export const WeightSelector = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${rem(space[6])};
`;

// Full-bleed divider between the weight selector and the product-selection
// instruction text. Negative horizontal margins cancel the body's GUTTER so
// the line spans the modal's full width, same technique as ScrollArea below.
export const ProductSelectionDivider = styled.div`
  flex-shrink: 0;
  border-top: ${hairline} solid ${tokens.color.border.subtle};
  margin: ${rem(space[16])} calc(-1 * ${GUTTER}) ${rem(space[12])};
`;

export const WeightLabel = styled.p`
  margin: 0;
  ${tokens.type.label[200]}
  color: ${tokens.color.text.label};
`;

export const WeightField = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${rem(space[12])};
  padding: ${rem(space[6])} ${rem(space[8])};
  border: ${hairline} solid ${tokens.color.border.subtle};
  border-radius: ${radius.base};
  background-color: transparent;
  cursor: pointer;
  ${tokens.type.label[300]}
  color: ${tokens.color.text.brand};
`;

export const WeightFieldChange = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(space[6])};
  ${tokens.type.label[200]}
  color: ${tokens.color.text.label};
`;

export const WeightFieldArrow = styled(ArrowDownSvg)`
  &&& {
    width: 11px;
    height: 6px;

    path {
      fill: ${tokens.color.border.focus};
    }
  }
`;

export const InstructionText = styled.p`
  flex-shrink: 0;
  margin: 0 0 ${rem(space[6])};
  ${tokens.type.label[200]}
  color: ${tokens.color.text.label};
`;

// Single scroll region for the product list. As a flex:1 child of the modal
// body (a flex column) it takes the space between the fixed filter header and
// the fixed Continue footer, so the list scrolls here while the header and
// footer stay put — the header is a plain flex item (no sticky) so it never
// travels on scroll, and the footer stays pinned at the modal bottom.
// Negative margins cancel the body's GUTTER so the scrollbar sits at the modal
// edge; the List re-adds that padding so rows stay inset symmetrically and
// clear the scrollbar. (Clipped by the body's overflow:hidden, so the
// horizontal bleed never adds its own scrollbar.)
export const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 calc(-1 * ${GUTTER});
`;

// Rows sit 10px apart in the frame (off the 4px scale).
export const List = styled.ul`
  list-style: none;
  padding: 0 ${GUTTER};
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
`;

// Empty product list. Mirrors the Price Alerts "Active Alerts" empty state
// (info icon + primary line + secondary line). height:100% so it fills the
// ScrollArea and centers vertically like the alerts-tab version. There is no
// frame for it, so it keeps its layout and takes library text styles.
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  text-align: center;
  gap: ${rem(space[12])};
  padding: ${rem(space[24])} 0;
`;

export const EmptyStateIcon = styled(InfoGttIconSvg)`
  width: 44px;
  height: 44px;
  margin: 12px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin: 8.25px;
  }
`;

export const EmptyStatePrimaryText = styled.p`
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};
  margin: 0;
`;

export const EmptyStateSecondaryText = styled.p`
  ${tokens.type.body[300]}
  color: ${tokens.color.text.secondary};
  margin: 0;
`;

// The weight-drawer's checkbox grid: two columns of filled cards.
export const WeightGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(space[12])};
  margin-top: ${rem(space[12])};
`;

// 12px padding around label/300 text: 44px at 320, growing with the text.
// The checkbox sits 10px from its label in the frame (off the 4px scale).
export const WeightOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  padding: ${rem(space[12])};
  border-radius: ${radius.base};
  background-color: ${tokens.color.surface.sunken};
  cursor: pointer;
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};
`;

// Full-width action button, used for the weight-drawer "Apply" footer and the
// screen's "Continue" footer.
export const FullWidthApply = styled.div`
  flex-shrink: 0;

  && button {
    width: 100%;
    ${wizardButtonCss}
  }
`;

export const ModalCloseIcon = styled(AlertCloseIconSvg)`
  &&& {
    width: 12.5px;
    height: 12.5px;
  }
`;

export const SearchIcon = styled(SearchSvg)`
  &&& {
    width: 19.52px;
    height: 19.52px;
    color: #000000;
  }
`;

// Selectable product row: radio dot · thumbnail · (name, price) · weight.
// The frame spaces the radio, thumbnail and text 10px apart (off the 4px
// scale) and the weight 20px from the text.
// At least the frame's 72px, so one-line names keep the row height.
export const ProductRow = styled.label`
  box-sizing: border-box;
  min-height: ${rem(72)};
  display: grid;
  grid-template-columns: auto 40px 1fr auto;
  align-items: center;
  gap: ${rem(10)};
  padding: ${rem(space[8])} ${rem(space[12])};
  border-radius: ${radius.base};
  background-color: ${tokens.color.surface.sunken};
  border: ${hairline} solid
    ${({ isSelected }) =>
      isSelected ? tokens.color.border.focus : 'transparent'};
  cursor: pointer;
`;

// The frame draws the radio's ring and dot in the focus border colour.
export const ProductRadio = styled.span`
  display: inline-flex;

  ${RadioCircle} {
    border-color: ${tokens.color.border.focus};

    &::after {
      background-color: ${tokens.color.border.focus};
    }
  }
`;

export const ProductThumb = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  object-fit: contain;
`;

export const ProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const ProductNameText = styled.span`
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};
`;

export const ProductPriceText = styled.span`
  ${tokens.type.title[300]}
  color: ${tokens.color.text.label};
`;

export const ProductWeightText = styled.span`
  margin-left: ${rem(10)};
  ${tokens.type.label[200]}
  color: ${tokens.color.text.label};
  white-space: nowrap;
`;

// --- Order-entry (TargetPriceOrderEntry) styled components ------------------

// Product name + live market price header row.
export const ProductHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(space[12])};
  margin-bottom: ${rem(space[16])};
`;

// The product name and its header price grow +4px per breakpoint (the
// library's feature styles).
export const ProductName = styled.span`
  ${tokens.type.labelFeature[400]}
  color: ${tokens.color.text.primary};
`;

export const ProductPrice = styled.span`
  ${tokens.type.titleFeature[400]}
  color: ${tokens.color.text.primary};
  white-space: nowrap;
`;

export const FieldLabel = styled.p`
  margin: 0 0 ${rem(space[6])};
  ${tokens.type.label[300]}
  color: ${tokens.color.text.label};
`;

// "Enter your quantity*" sits 24px under the below-market pills.
export const QuantityLabel = styled(FieldLabel)`
  margin-top: ${rem(space[24])};
`;

// "$"-prefixed currency input for the target price: 38px tall at 320, 52px
// from 375. The border only shows in the error state.
export const PriceInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: ${rem(38)};
  padding: 0 ${rem(space[8])};
  background: ${tokens.color.surface.sunken};
  border: ${hairline} solid
    ${({ hasError }) =>
      hasError ? tokens.color.status.danger : tokens.color.surface.sunken};
  border-radius: ${radius.base};

  ${media.up(typeBreakpoint[375])} {
    height: ${rem(52)};
  }
`;

export const DollarSign = styled.span`
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};
  margin-right: ${rem(space[4])};
`;

export const PriceInput = styled.input`
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  ${tokens.type.label[300]}
  color: ${tokens.color.text.primary};

  ::placeholder {
    color: ${tokens.color.text.tertiary};
  }
`;

export const PriceError = styled.p`
  margin: ${rem(space[4])} 0 0;
  ${tokens.type.body[300]}
  color: ${tokens.color.text.danger};
`;

// Puts the target-price input (each with its own label above it) and the
// "below market" quick-fill pills side by side in a 60/40 split, as two
// columns of a single row — guaranteeing both labels and both content
// blocks line up, rather than keeping two separate rows in sync. Stacks to
// a column on narrow widths where both can't fit on one row, 8px apart.
export const PriceAndBelowMarketRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(space[16])};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;
    align-items: stretch;
    gap: ${rem(space[8])};
  }
`;

export const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 3;
  min-width: 0;
`;

export const BelowMarketColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  min-width: 0;

  // "Recommended Selection" is dropped on mobile; the pills sit directly
  // under the price input with no label of their own.
  ${mediaBelow(variables.breakpoints.bpMd)} {
    ${FieldLabel} {
      display: none;
    }
  }
`;

// Vault-selection row wrapper. margin-top:auto pushes the vault row (and the
// footer below it) to the bottom of the flex-column modal body. position:
// relative gives BasicDivider (rendered as its first child) an anchor to pin
// itself to the wrapper's top edge, independent of the wrapper's own padding.
// The row itself is Buy's RowItem, whose spacing is scaled for the Buy flow,
// so it is re-spaced here to the frames: a 20px row 12px under the rule. Its
// data-cy is the only handle RowItem exposes.
export const VaultSelectionWrapper = styled.div`
  position: relative;
  margin-top: auto;
  padding-top: ${rem(space[12])};

  [data-cy='gtt-vault-row'] {
    height: auto;
    margin: 0 calc(-1 * ${GUTTER});
    padding: 0 ${GUTTER};

    > p:first-child {
      min-width: ${rem(space[64])};
    }

    > p:nth-child(2) {
      margin-right: ${rem(space[16])};
    }
  }
`;

// Full-bleed horizontal rule, absolutely pinned to the top edge of a
// position:relative parent regardless of that parent's own box model
// (padding, content height, etc.). left/right bleed through the body's
// gutter so the line spans the modal's full width.
export const BasicDivider = styled.div`
  position: absolute;
  top: 0;
  left: calc(-1 * ${GUTTER});
  right: calc(-1 * ${GUTTER});
  border-top: ${hairline} solid ${tokens.color.border.subtle};
`;

// The vault row's "Vault" label: body/300. display:flex + height:100%
// re-centers the text within RowItemLabel's own box.
export const VaultLabelText = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};
`;

// Vault value group: icon on the left, name, then the Best Price/Segregated
// badge. Centered by default so the icon and vault name line up with the
// row's other fields (label, clickLabel) in the common single-line case.
export const VaultValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(space[4])};
`;

// margin-right: 0 drops RowItemValue's own icon margin; VaultValue's gap
// spaces the icon instead.
export const VaultIcon = styled(VaultSvg)`
  &&& {
    width: 14.62px;
    height: 13.5px;
    margin-right: 0;
  }
`;

// The chosen vault's name (label/200), or before one is chosen the "select
// vault" placeholder (body/200 in the label colour), as in the frames.
export const VaultNameText = styled.span`
  white-space: nowrap;
  ${({ isPlaceholder }) =>
    isPlaceholder ? tokens.type.body[200] : tokens.type.label[200]}
  color: ${({ isPlaceholder }) =>
    isPlaceholder ? tokens.color.text.label : tokens.color.text.primary};
`;

// The vault row's "Select" / "Change" CTA text.
export const SelectCtaText = styled.span`
  ${tokens.type.body[200]}
  color: ${tokens.color.text.label};
`;

// "Best Price" badge: label/100 on the flag green, the one green the design
// uses. align-self: flex-start keeps it on the first line of a wrapped vault
// name.
export const BestPriceBanner = styled.span`
  align-self: flex-start;
  flex-shrink: 0;
  white-space: nowrap;
  padding: ${rem(3)} ${rem(space[2])};
  margin-left: ${rem(space[2])};
  border-radius: ${radius.sm};
  background-color: ${tokens.color.status.flag};
  ${tokens.type.label[100]}
  color: ${tokens.color.text.inverse};
`;

// Segregated Storage badge, shared by the Order Entry vault row (in place of
// the Best Price badge once Segregated is chosen), Review and Confirmation.
// Drawn like the designer's Best Price badge: label/100 on a 2px-radius chip,
// in the brand colour. align-self: center keeps it level with the vault name
// beside it.
export const SegregatedTag = styled.span`
  align-self: center;
  flex-shrink: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(3)} ${rem(space[2])};
  border-radius: ${radius.sm};
  background-color: ${tokens.color.action.primary};
  ${tokens.type.label[100]}
  color: ${tokens.color.text.inverse};
`;

// Footer row: "Buy at Current Price?" text link on the left, the primary
// "Review Target Order" button on the right. Normally sits 16px below
// VaultSelectionWrapper, which carries the margin-top:auto — but for
// instavault products (no vault row, so nothing else carries the auto
// margin) pinToBottom shifts that margin-top:auto onto the footer itself, so
// it's still pushed to the bottom instead of floating right under the
// quantity block.
export const OrderEntryFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(space[12])};
  margin-top: ${({ pinToBottom }) => (pinToBottom ? 'auto' : rem(space[16]))};
`;

// Dims the order-entry screen while a deposit wizard is open over the flow.
// Those wizards run with `overModal`, which makes their own backdrop
// transparent (so the GTT modal behind isn't double-dimmed) — that leaves the
// form fully lit, hence this local dimmer. Mirrors Buy/OrderSelection's
// BuyModalDimmer. The large z-index is scoped to GTT's own stacking context,
// so it still sits below the root-portaled deposit modal.
export const AddFundsDimmer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 9999999;
`;

export const BuyAtCurrentPriceLink = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;

  span {
    ${tokens.type.label[300]}
    color: ${tokens.color.text.brand};
    text-decoration: none;
  }

  &:hover span {
    text-decoration: underline;
  }
`;

// Wrapper so the primary "Review Target Order" button flexes to fill the space
// left over by the "Buy at Current Price?" link.
export const ReviewButtonWrapper = styled.div`
  flex: 1;

  button {
    width: 100%;
  }
`;

// Review Target Order / Add Funds button: the wizard's button text and
// padding, and a solid disabled background in place of the shared Button's
// disabled state (opacity: 0.5 dimming), scoped to this button only — every
// other button in the app keeps the default dimming. The extra & bumps
// specificity above Button's own primary-variant `:disabled:hover` rule
// (which resets hover back to the enabled colorButtonPrimary), so the
// disabled background stays put on hover too.
export const ReviewTargetOrderButton = styled(Button)`
  && {
    ${wizardButtonCss}
    // 12px sides, so "Review Target Order" fits on one line at 320 beside the
    // two-line "Buy at Current Price?", as in the frames. Narrower screens wrap.
    padding-left: ${rem(space[12])};
    padding-right: ${rem(space[12])};
  }

  &&:disabled {
    background: ${themeColors.colorButtonPrimaryDisabled};
    border-color: ${themeColors.colorButtonPrimaryDisabled};
    opacity: 1;

    &:hover,
    &:focus,
    &:active {
      background: ${themeColors.colorButtonPrimaryDisabled};
      border-color: ${themeColors.colorButtonPrimaryDisabled};
      box-shadow: none;
    }
  }
`;

// Quantity total + available-funds summary shown under the quantity stepper,
// mirroring the Buy flow's calculated-values block (see BuyInputFields).
export const QuantityCalcWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(space[12])};
`;

// "12 Units = $48,393.00": body/300, the amount in title/300.
export const QuantityCalcTotal = styled.p`
  ${tokens.type.body[300]}
  text-align: center;
  color: ${tokens.color.text.primary};
  margin: 0;

  b {
    ${tokens.type.title[300]}
  }
`;

// A short rule between the total and the available funds, 153px as in the
// frames.
export const QuantityCalcDivider = styled.div`
  width: ${rem(153)};
  max-width: 100%;
  border-bottom: ${hairline} solid ${tokens.color.border.subtle};
  margin: ${rem(space[6])} 0;
`;

// "Available Funds : $48,000.40": body/300, the amount in label/300.
export const AvailableFundsRow = styled.p`
  ${tokens.type.body[300]}
  text-align: center;
  color: ${tokens.color.text.label};
  margin: 0;

  b {
    ${tokens.type.label[300]}
    color: ${tokens.color.text.label};
  }
`;

// Shortfall line under Available Funds ("(+$393.40 required)"), shown only
// when the order would reserve more than the balance covers: title/300 in
// the warning colour.
export const AvailableFundsRequired = styled.p`
  ${tokens.type.title[300]}
  text-align: center;
  color: ${tokens.color.text.warning};
  margin: 0;
`;

// Row of "below market" quick-fill pills.
export const BelowMarketRow = styled.div`
  display: flex;
  gap: ${rem(space[8])};
  min-width: 0;
`;

// A "below market" quick-fill pill: the price over its "% Below" label, with
// 6px/16px padding so it grows with its text (50px tall at 320, 55px at 375,
// 60px at 430). The selected pill takes the brand border.
export const BelowMarketButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(space[2])};
  padding: ${rem(space[6])} ${rem(space[16])};
  border-radius: ${radius.base};
  border: ${hairline} solid
    ${({ isSelected }) =>
      isSelected ? tokens.color.action.primary : tokens.color.border.default};
  background-color: ${tokens.color.surface.sunken};
  cursor: pointer;
  box-shadow: none;
  transition: box-shadow 0.1s ease;

  // Content-sized pill on mobile instead of splitting the row's full width,
  // so the two buttons sit left-aligned rather than stretching edge to edge.
  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex: 0 0 auto;
    min-width: ${rem(96)};
  }

  // Side by side with the price input on desktop: same 52px height, so the
  // bottoms line up.
  ${media.up(typeBreakpoint.desktop)} {
    height: ${rem(52)};
    padding-top: 0;
    padding-bottom: 0;
  }

  // Drop-shadow appears on mouse down and is removed on mouse up for a
  // tactile click effect (:active spans the press).
  &:active {
    box-shadow: 0 ${rem(space[4])} ${rem(space[8])} rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const BelowMarketPrice = styled.span`
  ${tokens.type.title[300]}
  color: ${tokens.color.text.primary};
`;

export const BelowMarketLabel = styled.span`
  ${tokens.type.label[200]}
  color: ${tokens.color.status.flag};
`;

// Quantity stepper: [−] value [+], 38px tall at 320 and 52px from 375.
export const QuantityWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(space[12])};
  height: ${rem(38)};
  padding: 0 ${rem(space[8])};
  border-radius: ${radius.base};
  background: ${tokens.color.surface.sunken};

  ${media.up(typeBreakpoint[375])} {
    height: ${rem(52)};
  }
`;

// Bare 24px − / + icon buttons, as in the frames.
export const StepperButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(space[24])};
  height: ${rem(space[24])};
  padding: 0;
  border: 0;
  background-color: transparent;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// The source SVGs are a 28×28 canvas with the glyph centered, so filling the
// 24px button draws the plus glyph at ~14×14 and the minus bar at ~14×2.
// Sizing the element itself to 14×2/14×14 would letterbox the whole canvas and
// shrink the glyph to nothing. Recolor the path to the primary text colour.
export const StepperPlusIcon = styled(PlusIconSvg)`
  &&& {
    width: 24px;
    height: 24px;

    path {
      fill: ${tokens.color.text.primary};
    }
  }
`;

export const StepperMinusIcon = styled(MinusIconSvg)`
  &&& {
    width: 24px;
    height: 24px;

    path {
      fill: ${tokens.color.text.primary};
    }
  }
`;

// --- Order-review (TargetPriceOrderReview) styled components ---------------

// Product image beside the order text. The designer's frames grow the image
// with the text: 61px at 320, 80px from 375 and 90px from 430, with an 8px gap
// that opens to 12px. Desktop takes the 375 size, as the library's type does.
export const ReviewProductRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(space[8])};
  margin-bottom: ${rem(space[16])};

  ${media.up(typeBreakpoint[375])} {
    gap: ${rem(space[12])};
  }
`;

export const ReviewThumb = styled.img`
  width: 61px;
  height: 61px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 50%;

  ${media.up(typeBreakpoint[375])} {
    width: 80px;
    height: 80px;
  }

  ${media.up(typeBreakpoint[430])} {
    width: 90px;
    height: 90px;
  }

  ${media.up(typeBreakpoint.desktop)} {
    width: 80px;
    height: 80px;
  }
`;

export const ReviewProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(space[2])};
  min-width: 0;
`;

export const ReviewOrderLabel = styled.span`
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};
`;

export const ReviewProductName = styled.span`
  ${tokens.type.titleFeature[300]}
  color: ${tokens.color.text.primary};
`;

// "Storing in {vault} Vault": body text, with the vault name in the label
// weight. Review sets it at step 200 and Confirmation at 300, as in the frames.
export const ReviewVaultText = styled.span`
  ${({ textStep }) => tokens.type.body[textStep]}
  color: ${tokens.color.text.label};
`;

export const ReviewVaultName = styled.span`
  ${({ textStep }) => tokens.type.label[textStep]}
  color: ${tokens.color.text.label};
`;

// Shared "Storing in {vault} Vault" line used by both TargetPriceOrderReview
// and TargetPriceOrderConfirmation. Splits the translated sentence around
// the vault name so just that segment (+ "Vault") can render in
// ReviewVaultName's heavier weight, without hardcoding the surrounding copy.
export const StoringInVaultText = ({ translationKey, vaultName, textStep }) => {
  const { t } = useTranslation();
  const storingInText = t(translationKey, { vault: vaultName });
  const [storingInPrefix, storingInSuffix] = vaultName
    ? storingInText.split(vaultName)
    : [storingInText, ''];

  return (
    <ReviewVaultText textStep={textStep}>
      {storingInPrefix}
      {vaultName && (
        <ReviewVaultName textStep={textStep}>
          {vaultName}
          {storingInSuffix}
        </ReviewVaultName>
      )}
    </ReviewVaultText>
  );
};

StoringInVaultText.propTypes = {
  translationKey: PropTypes.string.isRequired,
  vaultName: PropTypes.string,
  textStep: PropTypes.oneOf([200, 300]),
};

StoringInVaultText.defaultProps = {
  vaultName: '',
  textStep: 200,
};

// Wraps StoringInVaultText + SegregatedTag so the segregated-storage badge
// sits inline with (and wraps under, on narrow widths) the vault sentence
// instead of stacking as its own block.
export const ReviewVaultRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(space[6])};
`;

// The price summary: dividers and rows 12px apart. The frames use 10-11px.
export const ReviewSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(space[12])};
`;

// A summary row with its caption 2px beneath it (Subtotal, Estd. Total).
export const ReviewSummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(space[2])};
`;

// Full-width horizontal rule between review sections (unlike
// QuantityCalcDivider, the short rule in the order-entry quantity summary).
// The frames draw the summary rules in border.default and the footer rule in
// border.subtle.
// fullBleed: bleeds past the body's gutter to span the modal's full width,
// same negative-margin technique as ProductSelectionDivider.
export const ReviewDivider = styled.div`
  width: 100%;
  border-bottom: ${hairline} solid
    ${({ fullBleed }) =>
      fullBleed ? tokens.color.border.subtle : tokens.color.border.default};

  ${({ fullBleed }) =>
    fullBleed &&
    `
    margin: 0 calc(-1 * ${GUTTER});
    width: auto;
  `}
`;

// Secondary single-line caption under the Subtotal / Total rows (e.g.
// "Current Price ($4,245.20)" / "Target Price ($4,032.75x12)") — not a
// label/value pair, so it isn't an InformationListItem.
export const PriceCaption = styled.p`
  margin: 0;
  ${tokens.type.body[200]}
  color: ${tokens.color.text.label};
`;

// Pushes the terms checkbox + submit button to the bottom of the flex-column
// modal body, mirroring VaultSelectionWrapper's margin-top:auto in the
// order-entry screen (ModalButtons' own built-in `margin-top: auto` gets
// overridden by its `marginTop` prop, so the push has to happen here instead).
// padding-top keeps at least 16px above the divider when the body is full.
export const ReviewFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(space[16])};
  margin-top: auto;
  padding-top: ${rem(space[16])};
`;

// Reserved-funds info banner. Left-aligned icon + multi-line text (unlike
// Buy/OrderConfirmation's centered single-line InfoBanner).
export const ReservedFundsBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(space[8])};
  margin-top: ${rem(space[12])};
  padding: ${rem(space[6])} ${rem(space[12])};
  border-radius: ${radius.base};
  background-color: ${tokens.color.surface.sunken};
  border: ${hairline} solid ${tokens.color.border.default};

  svg {
    flex-shrink: 0;
  }
`;

// The reserved amount is title/300-bold, the rest body/300.
export const ReservedFundsText = styled.p`
  margin: 0;
  ${tokens.type.body[300]}
  color: ${tokens.color.text.label};

  b {
    ${tokens.type.titleBold[300]}
  }
`;

// Volume-discount info banner. Bordered flag-green callout (unlike
// ReservedFundsBanner's neutral filled background) since the discount is
// contingent on execution rather than a settled figure. The frames keep it
// 96px tall at every width, its icon and text centred as one block.
export const VolumeDiscountBanner = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-content: center;
  align-items: start;
  column-gap: ${rem(space[8])};
  min-height: 96px;
  margin-top: ${rem(space[16])};
  padding: ${rem(space[8])};
  // The frame's pale border: the flag green at 25%, over a 5% fill.
  border: ${hairline} solid ${flagTint(0.25)};
  border-radius: ${radius.base};
  background-color: ${flagTint(0.05)};

  svg {
    margin-top: ${rem(space[2])};
  }
`;

// The SVG bakes in an old green; draw it in the flag colour like its text.
export const VolumeDiscountIcon = styled(DiscountSvg)`
  &&& {
    width: 15.75px;
    height: 15.75px;

    path {
      fill: ${tokens.color.status.flag};
    }
  }
`;

export const VolumeDiscountText = styled.p`
  margin: 0;
  ${tokens.type.title[300]}
  color: ${tokens.color.status.flag};
`;

// Terms checkbox row — mirrors PriceAlerts' AlertEntry SMS-consent row
// (CheckboxGTT + inline text/link) rather than the generic AgreementCheckbox
// used elsewhere, for visual parity between the two GTT flows.
export const ReviewTermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(space[6])};
`;

export const ReviewTermsText = styled.p`
  margin: 0;
  ${tokens.type.label[200]}
  color: ${tokens.color.text.label};
  text-align: left;
  overflow-wrap: break-word;
`;

export const ReviewTermsLink = styled.span`
  color: ${tokens.color.text.brand};
  cursor: pointer;
  text-decoration: underline;
`;

// --- Order-confirmation (TargetPriceOrderConfirmation) styled components ----

// setWizardTitle('') collapses BasicModal's own header on this last step, so
// the screen draws its own close affordance here instead (mirrors
// PriceAlerts' AlertConfirmation Container/CloseButton). On a phone the
// product image starts at the top, beside the close icon, as in the frames.
export const ConfirmationContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 ${GUTTER} ${GUTTER};
  background-color: ${tokens.color.surface.page};

  ${mediaUp(variables.breakpoints.bpMd)} {
    padding-top: ${GUTTER};
  }
`;

// 20px, level with the header's close icon on the other steps (8px header
// padding, centred in its 24px row).
export const ConfirmationCloseButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  position: absolute;
  top: ${rem(10)};
  right: ${GUTTER};
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

// Pushes the savings banner + notify text + button to the bottom of the
// flex-column screen, same margin-top:auto trick as ReviewFooterWrapper. The
// padding keeps a gap above the banner when a short screen leaves no room.
export const ConfirmationFooterWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
  padding-top: ${rem(space[16])};
`;

// 183px at 320 and 235px from 375, the designer's sizes. Short phones and
// desktop keep 183px.
export const ConfirmationProductImage = styled.img`
  width: 183px;
  height: 183px;
  object-fit: contain;
  border-radius: 50%;
  margin: 0 0 ${rem(space[8])};

  ${mediaUp(typeBreakpoint[375])} {
    width: 235px;
    height: 235px;
    margin-top: ${rem(space[32])};
  }

  // Short phones (iPhone SE and 8 are 667px tall): 235px would push View Order
  // off the screen.
  @media only screen and (min-width: ${typeBreakpoint[375]}px) and (max-height: 700px) {
    width: 183px;
    height: 183px;
    margin-top: 0;
  }

  ${mediaUp(variables.breakpoints.bpMd)} {
    width: 183px;
    height: 183px;
    margin: ${rem(space[16])} 0;
  }
`;

export const ConfirmationTitle = styled.p`
  ${tokens.type.label[600]}
  margin: 0 0 ${rem(space[12])};
  color: ${tokens.color.text.primary};
`;

export const ConfirmationProductLine = styled.p`
  ${tokens.type.titleFeature[600]}
  margin: 0 0 ${rem(space[2])};
  color: ${tokens.color.text.primary};
`;

export const ConfirmationSubText = styled.p`
  ${tokens.type.body[300]}
  margin: 0 0 ${rem(space[12])};
  color: ${tokens.color.text.label};
`;

// The vault line is centred here; a Segregated badge that wraps stays centred
// under it.
export const ConfirmationVaultRow = styled(ReviewVaultRow)`
  justify-content: center;
`;

// "You'd save (at execution) $x": icon, then the text with its amount in
// title/300-bold, centred together. The flag green throughout.
export const SavingsBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(space[8])};
  width: 100%;
  padding: ${rem(space[4])} ${rem(space[12])};
  margin-bottom: ${rem(space[16])};
  // The frame's pale border: the flag green at 25%, over a 5% fill.
  border: ${hairline} solid ${flagTint(0.25)};
  border-radius: ${radius.base};
  background-color: ${flagTint(0.05)};
  ${tokens.type.label[300]}
  color: ${tokens.color.status.flag};

  svg {
    flex-shrink: 0;
  }

  // base.js gives every span the primary text colour; take the banner's green.
  span {
    color: inherit;
  }

  b {
    ${tokens.type.titleBold[300]}
  }
`;

export const SavingsBannerIcon = styled(HandCoinsSvg)`
  width: 20px;
  height: 20px;
`;

export const ConfirmationNotifyText = styled.p`
  ${tokens.type.bodyItalic[200]}
  margin: 0 0 ${rem(space[8])};
  color: ${tokens.color.text.label};
`;

// "View Order": the outlined wizard button, its text and border in the brand
// colour.
export const ConfirmationButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  && button {
    ${wizardButtonCss}
    color: ${tokens.color.action.primary};
    border-color: ${tokens.color.action.primary};
  }
`;

// The quantity: display/700, as in the frames.
export const QuantityValue = styled.input`
  ${uOutlineNone};
  flex: 1;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  text-align: center;
  ${tokens.type.display[700]}
  color: ${tokens.color.text.primary};

  // Hide the native number-input spinners; the +/- buttons replace them.
  appearance: textfield;
  -moz-appearance: textfield;

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
